import MapView from "@arcgis/core/views/MapView";
import { fromUtm, load } from "@arcgis/core/geometry/coordinateFormatter";
import Point from "@arcgis/core/geometry/Point";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { project } from "@arcgis/core/geometry/projection";
import Graphic from "@arcgis/core/Graphic";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import Color from "@arcgis/core/Color";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import SearchSource from "@arcgis/core/widgets/Search/SearchSource";

export class UtmCoordinatesSearchSource {
  private _resultsLayer: GraphicsLayer;
  private _view: MapView;

  constructor(customSearchResultsLayer: GraphicsLayer, view: MapView) {
    this._resultsLayer = customSearchResultsLayer;
    this._view = view;
  }

  private async _search(
    term: { suggestTerm: string; sourceIndex: number },
    searchNotSuggest: boolean,
  ): Promise<any[]> {
    await load();

    if (
      !searchNotSuggest &&
      term &&
      term.suggestTerm &&
      !new RegExp("([0-9]+[A-z] [0-9]+ [0-9]+)").test(term.suggestTerm)
    ) {
      // If we're providing suggestions and the input term does not match the expected format, return a simple
      // "format does not match" 'suggestion.'
      let x = {
        sourceIndex: term.sourceIndex,
        extent: this._view.extent,
        name: "name", // Used for SearchResult
        key: "key", // used for search suggestions
        text: "Must match coordinate format: 13S 353782 3885199", // used for search suggestions
      };
      return [x];
    }

    let wgs84point: Point;
    try {
      wgs84point = fromUtm(
        term.suggestTerm,
        new SpatialReference({ wkid: 4326 }),
        "latitude-band-indicators",
      );
    } catch (_) {
      return [];
    }
    if (!(wgs84point?.x && wgs84point?.y)) {
      return [];
    }
    let point = project(wgs84point, this._view.spatialReference) as Point;
    // y.spatialReference = view.spatialReference;
    const graphic = new Graphic({
      geometry: point,
      popupTemplate: new PopupTemplate({ title: term.suggestTerm }),
      symbol: new SimpleMarkerSymbol({
        color: new Color("#73ff4b"),
      }),
    });
    if (searchNotSuggest) this._resultsLayer.add(graphic);
    let x = {
      feature: graphic,
      target: graphic,
      sourceIndex: term.sourceIndex,
      extent: this._view.extent,
      name: term.suggestTerm, // Used for SearchResult
      key: term.suggestTerm, // used for search suggestions
      text: term.suggestTerm, // used for search suggestions
    };
    return [x];
  }

  searchSource(): Partial<SearchSource & { name: string }> {
    return {
      getSuggestions: (params: {
        suggestTerm: string;
        sourceIndex: number;
      }) => this._search(params, false),
      getResults: (params: {
        suggestResult: {
          text: string;
          sourceIndex: number;
        };
      }) =>
        this._search(
          {
            suggestTerm: params.suggestResult.text,
            sourceIndex: params.suggestResult.sourceIndex,
          },
          true,
        ),
      name: "UTM Coordinates",
      placeholder: "13S 353782 3885199",
      suggestionsEnabled: true,
      minSuggestCharacters: 3,
      maxSuggestions: 1,
      maxResults: 1,
    };
  }
}
